import { Spinner } from '@frontend/components/common';
import { IGroup } from '@frontend/group/types';
import { CommonMessage, getCorrectTranslation, useLocale } from '@frontend/lang';
import { IProduct } from '@frontend/product/types';
import type { ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';

import WizardScreenContainer from '../../components/wizard-screen-container/wizard-screen-container.component';
import { CategoryCard } from '../browse-products-by-categories/category-card/category-card.component';
import { LeafCategoryCard } from '../browse-products-by-categories/leaf-category-card/leaf-category-card.component';
import { ProductCard } from '../browse-products-by-categories/product-card/product-card.component';
import useRequestServices from './request-services.controller';

const ID = 'request-services';

const RequestServices = () => {
    const viewProps = useRequestServices();
    const { locale } = useLocale();

    if (viewProps.isLoading) {
        return (
            <WizardScreenContainer
                id={ID}
                footer={viewProps.canGoBack ? { onBack: viewProps.navigateBack } : undefined}
                header={{ title: CommonMessage.OBJECTS.SERVICE.PLURAL, onBack: viewProps.canGoBack ? viewProps.navigateBack : undefined }}>
                <div className='flex h-full w-full items-center justify-center py-12'>
                    <Spinner />
                </div>
            </WizardScreenContainer>
        );
    }

    const hasCategories = viewProps.branchCategories.length > 0 || viewProps.leafCategories.length > 0;
    const hasServices = viewProps.services.length > 0;

    return (
        <WizardScreenContainer
            id={ID}
            footer={viewProps.canGoBack ? { onBack: viewProps.navigateBack } : undefined}
            header={{ title: CommonMessage.OBJECTS.SERVICE.PLURAL, onBack: viewProps.canGoBack ? viewProps.navigateBack : undefined }}>
            {viewProps.currentCategory && (
                <div className='text-foreground-default text-lg font-semibold'>{getCategoryName(viewProps.currentCategory, locale)}</div>
            )}

            {hasCategories && (
                <div className='inline-flex flex-col items-start justify-start gap-3 self-stretch'>
                    <p className='font-medium'>{CommonMessage.OBJECTS.CATEGORY.PLURAL}</p>
                    {viewProps.branchCategories.length > 0 && (
                        <div className='grid w-full grid-cols-2 gap-2 self-stretch'>
                            {viewProps.branchCategories.map((category) => (
                                <CategoryCard
                                    key={category.id}
                                    category={category}
                                    onClick={viewProps.navigateToCategory}
                                />
                            ))}
                        </div>
                    )}
                    {viewProps.leafCategories.length > 0 && (
                        <div className='flex w-full flex-col gap-2 self-stretch'>
                            {viewProps.leafCategories.map((category) => (
                                <LeafCategoryCard
                                    key={category.id}
                                    category={category}
                                    onClick={viewProps.navigateToCategory}
                                />
                            ))}
                        </div>
                    )}
                </div>
            )}

            {hasServices && (
                <div className='inline-flex flex-col items-start justify-start gap-3 self-stretch'>
                    <p className='font-medium'>{CommonMessage.OBJECTS.SERVICE.PLURAL}</p>
                    <div className='flex flex-col gap-2 self-stretch'>
                        {viewProps.services.map((service) => (
                            <ProductCard
                                key={service.id}
                                product={service}
                                onClick={viewProps.onSelectService}
                                description={getServiceDescription(service, locale)}
                                styles={{
                                    container: viewProps.selectedServiceProductId === service.id ? '!border-brand-primary border-2' : ''
                                }}
                            />
                        ))}
                    </div>
                </div>
            )}

            {!hasCategories && !hasServices && (
                <div className='text-brand-muted flex h-full w-full items-center justify-center text-center text-base font-medium'>
                    <RequestServicesEmptyStateMessage emptyState={viewProps.emptyState} />
                </div>
            )}
        </WizardScreenContainer>
    );
};

const RequestServicesEmptyStateMessage = (props: { emptyState: ReturnType<typeof useRequestServices>['emptyState'] }) => {
    switch (props.emptyState) {
        case 'missing_root_category_setting':
            return (
                <FormattedMessage
                    id='kiosk.wizard.request-services.catalog-not-configured'
                    description='Configuration error shown on the request services screen when the required service catalog root category setting is missing'
                    defaultMessage='The service catalog has not been configured.'
                />
            );
        case 'missing_account':
            return (
                <FormattedMessage
                    id='kiosk.wizard.request-services.catalog-unavailable'
                    description='Configuration error shown on the request services screen when no account context is available'
                    defaultMessage='The service catalog is not available right now.'
                />
            );
        case 'load_failed':
            return (
                <FormattedMessage
                    id='kiosk.wizard.request-services.load-failed'
                    description='Error shown on the request services screen when the service catalog request fails'
                    defaultMessage='The service catalog could not be loaded.'
                />
            );
        case 'no_data':
        default:
            return (
                <FormattedMessage
                    id='kiosk.wizard.request-services.no-services'
                    description='Empty state shown on the request services screen when the configured catalog has no service products'
                    defaultMessage='No services are available in this catalog.'
                />
            );
    }
};

const getCategoryName = (category: IGroup, locale: ReturnType<typeof useLocale>['locale']): string => {
    return getCorrectTranslation(locale, category, 'name') || category.name;
};

const getServiceDescription = (service: IProduct, locale: ReturnType<typeof useLocale>['locale']): ReactNode => {
    const description = getCorrectTranslation(locale, service, 'description') || service.description;
    if (typeof description !== 'string') return undefined;

    const trimmed = description.trim();
    if (trimmed.length === 0) return undefined;

    return <p className='text-brand-muted line-clamp-3 text-sm font-medium'>{trimmed}</p>;
};

export default RequestServices;
